<template>
  <q-layout view="lHh Lpr lFf">
    <q-page-container>
      <q-page class="auth-page flex flex-center">
        <div class="auth-wrap">
          <!-- Brand -->
          <div class="brand-section">
            <div class="brand-logo">
              <img src="../assets/icon2.png" alt="GoldenKey" />
            </div>
            <div class="brand-name">GoldenKey</div>
            <div class="brand-tagline">Portal Imobiliário</div>
          </div>

          <!-- Card -->
          <div class="auth-card">
            <div class="auth-card-header">
              <div class="auth-title">Bem-vindo de volta</div>
              <div class="auth-sub">Faça login para aceder à sua conta</div>
            </div>

            <q-form @submit="handleLogin" class="auth-form">
              <!-- Email -->
              <div class="field-wrap">
                <label class="field-label">Email</label>
                <q-input
                  v-model="form.email"
                  type="email"
                  outlined
                  dense
                  placeholder="o-seu@email.com"
                  :rules="[
                    (val) => !!val || 'Email é obrigatório',
                    (val) => /.+@.+\..+/.test(val) || 'Email inválido',
                  ]"
                  lazy-rules
                  :error="!!errors.email"
                  :error-message="errors.email"
                  class="auth-input"
                >
                  <template v-slot:prepend>
                    <q-icon name="mail_outline" size="18px" />
                  </template>
                </q-input>
              </div>

              <!-- Password -->
              <div class="field-wrap">
                <label class="field-label">Senha</label>
                <q-input
                  v-model="form.password"
                  :type="showPassword ? 'text' : 'password'"
                  outlined
                  dense
                  placeholder="••••••••"
                  :rules="[
                    (val) => !!val || 'Senha é obrigatória',
                    (val) => val.length >= 6 || 'Mínimo 6 caracteres',
                  ]"
                  lazy-rules
                  :error="!!errors.password"
                  :error-message="errors.password"
                  class="auth-input"
                >
                  <template v-slot:prepend>
                    <q-icon name="lock_outline" size="18px" />
                  </template>
                  <template v-slot:append>
                    <q-icon
                      :name="showPassword ? 'visibility_off' : 'visibility'"
                      size="18px"
                      class="cursor-pointer"
                      @click="showPassword = !showPassword"
                    />
                  </template>
                </q-input>
              </div>

              <!-- Remember + Forgot -->
              <div class="form-meta">
                <q-checkbox
                  v-model="rememberMe"
                  label="Lembrar-me"
                  dense
                  class="meta-check"
                />
                <button
                  type="button"
                  class="forgot-link"
                  @click="handleForgotPassword"
                >
                  Esqueceu a senha?
                </button>
              </div>

              <!-- Submit -->
              <button type="submit" class="submit-btn" :disabled="loading">
                <q-spinner-dots v-if="loading" color="white" size="20px" />
                <template v-else>
                  <span>Entrar</span>
                  <q-icon name="arrow_forward" size="18px" />
                </template>
              </button>

              <!-- Divider -->
              <div class="auth-divider"><span>ou</span></div>

              <!-- Google -->
              <button
                type="button"
                class="google-btn"
                :disabled="loading"
                @click="loginWithGoogle"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 48 48"
                >
                  <path
                    fill="#FFC107"
                    d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
                  />
                  <path
                    fill="#FF3D00"
                    d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
                  />
                  <path
                    fill="#4CAF50"
                    d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
                  />
                  <path
                    fill="#1976D2"
                    d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
                  />
                </svg>
                <span>Entrar com Google</span>
              </button>
            </q-form>

            <div class="auth-card-footer">
              Não tem uma conta?
              <button type="button" class="link-btn" @click="handleRegister">
                Criar conta
              </button>
            </div>
          </div>

          <!-- Footer -->
          <div class="page-footer">
            © {{ new Date().getFullYear() }} Plataforma Imobiliária · Um produto
            da
            <a href="https://marrar.co.mz" target="__blank">Marrar, LDA</a>
          </div>
        </div>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useQuasar } from "quasar";
import router from "@/router";
import store from "@/vuex/store";
import Swal from "sweetalert2";
import { getAuth, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { getToken, removeToken, setToken } from "@/utils/tokenStorage";

const auth = getAuth();

// eslint-disable-next-line no-unused-vars
const $q = useQuasar();

const form = ref({
  email: "",
  password: "",
});

const errors = ref({
  email: "",
  password: "",
});

const showPassword = ref(false);
const rememberMe = ref(false);
const loading = ref(false);

onMounted(async () => {
  if (!getToken()) return;
  try {
    await store.dispatch("auth/getMe");
    router.replace("/imoveis");
  } catch {
    removeToken();
  }
});

const loginWithGoogle = async () => {
  loading.value = true;
  try {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    // console.log(credential)
    // console.log(result.idToken)
    const user = result.user;
    const res = await store.dispatch(
      "auth/loginWithGoogle",
      credential.accessToken,
    );
    setToken(res.access_token, false);
    console.log("Google Sign-In bem-sucedido:", user);
    router.push("/imoveis");
    loading.value = false;
    // Aqui você pode redirecionar o usuário ou armazenar as informações necessárias
  } catch (error) {
    loading.value = false;
    console.error("Erro no Google Sign-In:", error);
    Swal.fire({
      icon: "error",
      title: "Erro no Google Sign-In",
      text: "Ocorreu um erro ao tentar fazer login com o Google. Por favor, tente novamente.",
    });
  }
};

const handleLogin = async () => {
  loading.value = true;
  errors.value = { email: "", password: "" };

  try {
    const res = await store.dispatch("auth/login", {
      email: form.value.email,
      password: form.value.password,
      remember_me: rememberMe.value,
    });
    setToken(res.access_token, rememberMe.value);
    // console.log('Login bem-sucedido:', store.state.auth.user)

    router.push("/imoveis");
  } catch (error) {
    // console.error('Erro no login:', error)
    Swal.fire({
      icon: "warning",
      title: "Erro no login",
      text: "Credenciais inválidas. Por favor, tente novamente.",
    });
  } finally {
    loading.value = false;
  }
};

const handleForgotPassword = () => {
  router.push({ name: "ResetPassword" });
  // router.push('/forgot-password')
};

const handleRegister = () => {
  router.push({ name: "AutoRegister" });
};
</script>

<style scoped>
/* ── Page ──────────────────────────────────────────── */
.auth-page {
  background: #0d1b2a;
  min-height: 100vh;
  padding: 32px 16px;
}

/* ── Wrap ──────────────────────────────────────────── */
.auth-wrap {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* ── Brand ─────────────────────────────────────────── */
.brand-section {
  text-align: center;
  margin-bottom: 28px;
}
.brand-logo {
  width: 64px;
  height: 64px;
  margin: 0 auto 12px;
}
.brand-logo img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.brand-name {
  font-size: 22px;
  font-weight: 800;
  color: #d4af37;
  letter-spacing: 0.5px;
}
.brand-tagline {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-top: 3px;
}

/* ── Card ──────────────────────────────────────────── */
.auth-card {
  width: 100%;
  background: #fff;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.35);
}
.auth-card-header {
  margin-bottom: 26px;
}
.auth-title {
  font-size: 20px;
  font-weight: 800;
  color: #0d1b2a;
  margin-bottom: 4px;
}
.auth-sub {
  font-size: 13px;
  color: #94a3b8;
}

/* ── Form ──────────────────────────────────────────── */
.auth-form {
  display: flex;
  flex-direction: column;
}
.field-wrap {
  margin-bottom: 14px;
}
.field-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 5px;
  letter-spacing: 0.2px;
}

/* Input overrides */
:deep(.auth-input .q-field__control) {
  border-radius: 12px;
}
:deep(.auth-input .q-field__control:before) {
  border-color: #e2e8f0;
}
:deep(.auth-input .q-field__control:hover:before) {
  border-color: #0d1b2a;
}
:deep(.auth-input.q-field--focused .q-field__control:before) {
  border-color: #d4af37 !important;
  border-width: 2px;
}
:deep(.auth-input .q-field__prepend),
:deep(.auth-input .q-field__append) {
  color: #b0bec5;
}
:deep(.auth-input.q-field--focused .q-field__prepend) {
  color: #0d1b2a;
}
:deep(.auth-input input::placeholder) {
  color: #c8d0d8;
}

/* Meta row */
.form-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  margin-top: 2px;
}
:deep(.meta-check .q-checkbox__label) {
  font-size: 13px;
  color: #64748b;
}
:deep(.meta-check .q-checkbox__inner) {
  color: #0d1b2a;
}
.forgot-link {
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 600;
  color: #d4af37;
  cursor: pointer;
  padding: 0;
  transition: opacity 0.15s;
}
.forgot-link:hover {
  opacity: 0.75;
  text-decoration: underline;
}

/* Submit button */
.submit-btn {
  width: 100%;
  padding: 13px;
  border-radius: 12px;
  background: #0d1b2a;
  color: #d4af37;
  border: none;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.18s, transform 0.18s;
  letter-spacing: 0.3px;
}
.submit-btn:hover:not(:disabled) {
  background: #162333;
  transform: translateY(-1px);
}
.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Divider */
.auth-divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 18px 0;
  color: #cbd5e1;
  font-size: 12px;
  font-weight: 500;
}
.auth-divider::before,
.auth-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #e8ecf4;
}

/* Google button */
.google-btn {
  width: 100%;
  padding: 12px;
  border-radius: 12px;
  background: #fff;
  border: 1.5px solid #e2e8f0;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: border-color 0.18s, background 0.18s;
}
.google-btn:hover:not(:disabled) {
  border-color: #94a3b8;
  background: #f8fafc;
}
.google-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Card footer */
.auth-card-footer {
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid #f1f5f9;
  text-align: center;
  font-size: 13px;
  color: #64748b;
}
.link-btn {
  background: none;
  border: none;
  font-size: 13px;
  font-weight: 700;
  color: #0d1b2a;
  cursor: pointer;
  padding: 0;
  margin-left: 4px;
  transition: color 0.15s;
}
.link-btn:hover {
  color: #d4af37;
}

/* Page footer */
.page-footer {
  margin-top: 20px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.28);
  text-align: center;
  line-height: 1.7;
}
.page-footer a {
  color: rgba(212, 175, 55, 0.55);
  text-decoration: none;
  transition: color 0.15s;
}
.page-footer a:hover {
  color: #d4af37;
}
</style>
